import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { FormattedNumber, FormattedNumberParts } from 'react-intl'
import _ from 'lodash'

import './style.scss'

// Reference :
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
// https://formatjs.io/docs/react-intl/components#formattednumber

const Number = ({
  value,
  extraClasses,
  currency,
  signDisplay,
  redIsRaise,
  fluctuationColor,
  fractionDigits,
  style: formatStyle,
  minimumFractionDigits,
  maximumFractionDigits,
  children,
  id,
  ...restProps
}) => {
  // eslint-disable-next-line no-restricted-globals
  const fixedFormattedValue = _.isNaN(parseFloat(value)) ? value : parseFloat(value)
  const convertedValue = !!value || fixedFormattedValue === 0 ? fixedFormattedValue : '-'
  const formattedMinimumFractionDigits = minimumFractionDigits ?? fractionDigits
  const formattedMaximumFractionDigits = maximumFractionDigits ?? fractionDigits

  let isZero = false
  if (convertedValue !== '-') {
    const tempMinValue = parseFloat(convertedValue * (formatStyle === 'percent' ? 100 : 1)).toFixed(formattedMinimumFractionDigits)
    const tempMaxValue = parseFloat(convertedValue * (formatStyle === 'percent' ? 100 : 1)).toFixed(formattedMaximumFractionDigits)
    isZero = parseFloat(tempMinValue) === 0 && parseFloat(tempMaxValue) === 0
  }

  const elementProps = {
    value: convertedValue,
    minimumFractionDigits: formattedMinimumFractionDigits,
    maximumFractionDigits: formattedMaximumFractionDigits,
    signDisplay: isZero ? 'never' : convertedValue ? signDisplay : 'never',
    style: formatStyle,
    ...currency,
    ...restProps
  }

  return (
    <React.Fragment>
      {convertedValue !== '-' ? (
        <div
          {...(id ? { id } : {})}
          data-hidden-value={convertedValue}
          className={classnames(extraClasses, {
            positive: fluctuationColor && !isZero && (redIsRaise ? convertedValue < 0 : convertedValue > 0),
            negative: fluctuationColor && !isZero && (redIsRaise ? convertedValue > 0 : convertedValue < 0)
          })}
        >
          {children ? (
            <FormattedNumberParts {...elementProps}>{(parts) => children(parts)}</FormattedNumberParts>
          ) : (
            <FormattedNumber {...elementProps} />
          )}
        </div>
      ) : (
        <div>{convertedValue}</div>
      )}
    </React.Fragment>
  )
}

Number.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  id: PropTypes.string,
  currency: PropTypes.shape({
    currency: PropTypes.string,
    currencyDisplay: PropTypes.oneOf(['symbol', 'narrowSymbol', 'code', 'name']),
    currencySign: PropTypes.string
  }),
  signDisplay: PropTypes.oneOf(['auto', 'never', 'always', 'exceptZero']),
  fractionDigits: PropTypes.number,
  minimumFractionDigits: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  maximumFractionDigits: PropTypes.number,
  fluctuationColor: PropTypes.bool,
  extraClasses: PropTypes.string,
  redIsRaise: PropTypes.bool,
  style: PropTypes.string,
  children: PropTypes.func
}

Number.defaultProps = {
  id: '',
  currency: {},
  signDisplay: 'auto',
  fractionDigits: 2,
  minimumFractionDigits: null,
  maximumFractionDigits: null,
  fluctuationColor: true,
  extraClasses: '',
  redIsRaise: false,
  style: 'decimal',
  children: null
}

export default Number
